import { ExternalLink } from "lucide-react";
import BaseDrawer from "@/shared/components/Drawer";
import { ACCOUNTING_DATE_BOUNDS } from "@/shared/configs/accountingDateBounds";
import { Button } from "@/shared/ui/Button";
import { Input } from "@/shared/ui/Input";
import AllocationRoomHint from "./AllocationRoomHint";
import {
  CREDIT_LOCKED_HINT,
  DEBIT_LOCKED_HINT,
  LOCKED_AMOUNT_INPUT_CLASS,
  formatAmount,
  formatCurrencyField,
  getAmountSideLock,
  normalizeCurrencyTyping,
} from "./utils";
import type { EditableAllocationState } from "./utils";
import type { LedgerAllocation } from "../../types/accounting.types";

type ReimbursementAllocationDetailProps = {
  open: boolean
  onClose: () => void
  selectedAllocation: LedgerAllocation | null
  simpleEditorRow: EditableAllocationState | null
  setField: (id: string, field: keyof EditableAllocationState, value: string, fallback: LedgerAllocation) => void
  isSimpleEditorBusy: boolean
  onSave: () => void
  onOpenSettlement?: (settlementId: string) => void
};

const formatDate = (iso: string | null): string => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }
  catch {
    return iso;
  }
};

const ReimbursementAllocationDetail = ({
  open,
  onClose,
  selectedAllocation,
  simpleEditorRow,
  setField,
  isSimpleEditorBusy,
  onSave,
  onOpenSettlement,
}: ReimbursementAllocationDetailProps) => {
  const { isDebitLocked, isCreditLocked } = getAmountSideLock(simpleEditorRow?.debit, simpleEditorRow?.credit);
  return (
    <BaseDrawer
      drawerId="DRW-REMB"
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
      isFirstLevel={false}
      onBack={onClose}
      width="893px"
      zIndex={65}
      bodyClassName="[&_*]:select-text"
      title={`💰 Écriture remboursement${selectedAllocation?.refundId ? ` — R${selectedAllocation.refundId}` : ""}`}
      description="Affectation liée à un remboursement salarié."
    >
      {/* Reimbursement info section */}
      {selectedAllocation && (
        <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 mb-4 space-y-3">
          <div className="text-[12px] font-medium text-amber-900">Informations remboursement</div>

          {/* Identification */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
            {selectedAllocation.refundId && (
              <div>
                <span className="text-amber-700">Demande :</span>
                {" "}
                <span className="font-medium text-amber-950">
                  R
                  {selectedAllocation.refundId}
                </span>
              </div>
            )}
            {selectedAllocation.refundCreatedAt && (
              <div>
                <span className="text-amber-700">Date demande :</span>
                {" "}
                <span className="font-medium text-amber-950">{formatDate(selectedAllocation.refundCreatedAt)}</span>
              </div>
            )}
            {selectedAllocation.refundClientName && (
              <div>
                <span className="text-amber-700">Bénéficiaire :</span>
                {" "}
                <span className="font-medium text-amber-950">{selectedAllocation.refundClientName}</span>
              </div>
            )}
            {selectedAllocation.refundClientSite && (
              <div>
                <span className="text-amber-700">Site :</span>
                {" "}
                <span className="font-medium text-amber-950">{selectedAllocation.refundClientSite}</span>
              </div>
            )}
            {selectedAllocation.refundReferenceName && (
              <div className="md:col-span-2">
                <span className="text-amber-700">Objet :</span>
                {" "}
                <span className="font-medium text-amber-950">{selectedAllocation.refundReferenceName}</span>
              </div>
            )}
            {selectedAllocation.refundStatusName && (
              <div className="md:col-span-2">
                <span className="text-amber-700">Statut :</span>
                {" "}
                <span className="inline-flex items-center rounded-full bg-amber-200/70 px-2 py-0.5 text-[11px] font-medium text-amber-900 ml-1">
                  {selectedAllocation.refundStatusName}
                </span>
              </div>
            )}
          </div>

          {/* IBAN / BIC */}
          {(selectedAllocation.refundIban || selectedAllocation.refundBic) && (
            <div className="border-t border-amber-200 pt-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
              {selectedAllocation.refundIban && (
                <div>
                  <span className="text-amber-700">IBAN :</span>
                  {" "}
                  <span className="font-medium tabular-nums text-amber-950">{selectedAllocation.refundIban}</span>
                </div>
              )}
              {selectedAllocation.refundBic && (
                <div>
                  <span className="text-amber-700">BIC :</span>
                  {" "}
                  <span className="font-medium tabular-nums text-amber-950">{selectedAllocation.refundBic}</span>
                </div>
              )}
            </div>
          )}

          {/* Montants */}
          {(selectedAllocation.refundAmountRetained !== null || selectedAllocation.refundAmountPaid !== null || selectedAllocation.refundAllocationBalance !== null) && (
            <div className="border-t border-amber-200 pt-2 grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-1.5 text-[12px]">
              {selectedAllocation.refundAmountRetained !== null && (
                <div>
                  <span className="text-amber-700">Montant retenu :</span>
                  {" "}
                  <span className="font-medium tabular-nums text-amber-950">
                    {formatAmount(selectedAllocation.refundAmountRetained)}
                    {" "}
                    €
                  </span>
                </div>
              )}
              {selectedAllocation.refundAmountPaid !== null && (
                <div>
                  <span className="text-amber-700">Montant payé :</span>
                  {" "}
                  <span className="font-medium tabular-nums text-amber-950">
                    {formatAmount(selectedAllocation.refundAmountPaid)}
                    {" "}
                    €
                  </span>
                </div>
              )}
              {selectedAllocation.refundAllocationBalance !== null && (
                <div>
                  <span className="text-amber-700">Solde :</span>
                  {" "}
                  <span className="font-medium tabular-nums text-amber-950">
                    {formatAmount(selectedAllocation.refundAllocationBalance)}
                    {" "}
                    €
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Tableau bénéficiaires */}
          {selectedAllocation.refundBeneficiaries.length > 0 && (
            <div className="border-t border-amber-200 pt-2 text-[12px]">
              <span className="text-amber-700 font-medium">Bénéficiaires</span>
              <div className="mt-1.5 overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-amber-200 text-amber-700">
                      <th className="py-1 pr-3 font-medium">Nom</th>
                      <th className="py-1 pr-3 font-medium">Type</th>
                      <th className="py-1 pr-3 font-medium text-right">Montant facture</th>
                      <th className="py-1 pr-3 font-medium text-right">Unité facture</th>
                      <th className="py-1 pr-3 font-medium text-right">Unités sollicitées</th>
                      <th className="py-1 font-medium text-right">Montant retenu</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedAllocation.refundBeneficiaries.map((b) => (
                      <tr key={b.id} className="border-b border-amber-100 last:border-0">
                        <td className="py-1 pr-3 font-medium text-amber-950">{b.name}</td>
                        <td className="py-1 pr-3 text-amber-700">{b.holderType === "beneficiary" ? "Ayant droit" : b.holderType === "employee" ? "Salarié" : ""}</td>
                        <td className="py-1 pr-3 tabular-nums text-right text-amber-950">{b.invoiceAmount !== null ? `${formatAmount(b.invoiceAmount)} €` : ""}</td>
                        <td className="py-1 pr-3 tabular-nums text-right text-amber-950">{b.invoiceUnitAmount !== null ? b.invoiceUnitAmount : ""}</td>
                        <td className="py-1 pr-3 tabular-nums text-right text-amber-950">{b.unitsRequested !== null ? b.unitsRequested : ""}</td>
                        <td className="py-1 tabular-nums text-right text-amber-950">{b.amountRetained !== null ? `${formatAmount(b.amountRetained)} €` : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pièces jointes */}
          {selectedAllocation.refundAttachments.length > 0 && (
            <div className="border-t border-amber-200 pt-2 text-[12px]">
              <span className="text-amber-700 font-medium">Pièces jointes</span>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {selectedAllocation.refundAttachments.map((attachment) => (
                  <a
                    key={attachment.url}
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-white px-2.5 py-1.5 text-amber-900 hover:bg-amber-100 transition-colors"
                  >
                    <span className="text-[13px]">{attachment.contentType.startsWith("image/") ? "🖼️" : "📄"}</span>
                    <span>{attachment.fileName || attachment.label}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Settlement info section */}
      {selectedAllocation?.settlementId && (
        <div className="rounded-md border border-indigo-200 bg-indigo-50/60 p-3 mb-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[12px] font-medium text-indigo-900">Règlement associé</div>
            {onOpenSettlement && (
              <button
                type="button"
                onClick={() => onOpenSettlement(selectedAllocation.settlementId!)}
                className="inline-flex items-center gap-1 text-[11px] text-indigo-600 hover:text-indigo-800 underline underline-offset-2"
              >
                Voir le règlement
                <ExternalLink className="h-3 w-3" />
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
            {selectedAllocation.settlementNumber && (
              <div>
                <span className="text-indigo-700">N° :</span>
                {" "}
                <span className="font-medium text-indigo-950">{selectedAllocation.settlementNumber}</span>
              </div>
            )}
            {selectedAllocation.settlementDate && (
              <div>
                <span className="text-indigo-700">Date :</span>
                {" "}
                <span className="font-medium text-indigo-950">{formatDate(selectedAllocation.settlementDate)}</span>
              </div>
            )}
            {selectedAllocation.settlementIssuer && (
              <div>
                <span className="text-indigo-700">Émetteur :</span>
                {" "}
                <span className="font-medium text-indigo-950">{selectedAllocation.settlementIssuer}</span>
              </div>
            )}
            {selectedAllocation.settlementPaymentMethod && (
              <div>
                <span className="text-indigo-700">Support :</span>
                {" "}
                <span className="font-medium text-indigo-950">{selectedAllocation.settlementPaymentMethod}</span>
              </div>
            )}
            {selectedAllocation.settlementDeferredDate && (
              <div>
                <span className="text-indigo-700">Date différée :</span>
                {" "}
                <span className="font-medium text-indigo-950">{formatDate(selectedAllocation.settlementDeferredDate)}</span>
              </div>
            )}
          </div>
          {(selectedAllocation.settlementAmount !== null || selectedAllocation.settlementRemainingAmount !== null) && (
            <div className="border-t border-indigo-200 pt-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
              {selectedAllocation.settlementAmount !== null && (
                <div>
                  <span className="text-indigo-700">Montant :</span>
                  {" "}
                  <span className="font-medium tabular-nums text-indigo-950">
                    {formatAmount(selectedAllocation.settlementAmount)}
                    {" "}
                    €
                  </span>
                </div>
              )}
              {selectedAllocation.settlementRemainingAmount !== null && (
                <div>
                  <span className="text-indigo-700">Solde :</span>
                  {" "}
                  <span className="font-medium tabular-nums text-indigo-950">
                    {formatAmount(selectedAllocation.settlementRemainingAmount)}
                    {" "}
                    €
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Opérations du remboursement */}
      {selectedAllocation && selectedAllocation.refundOperations.length > 0 && (
        <div className="rounded-md border border-slate-200 bg-white p-3 mb-4 space-y-3 text-[12px]">
          <div className="font-medium text-slate-700">Opérations du remboursement</div>
          {selectedAllocation.refundOperations.map((op) => {
            const isParentOp = String(op.id) === String(selectedAllocation.activityOperationId);
            const siblingAllocations = op.allocations.filter((a) => a.id !== selectedAllocation.id);
            return (
              <div
                key={op.id}
                className={`rounded-md border p-2 space-y-1.5 ${isParentOp ? "border-amber-300 bg-amber-50/60" : "border-slate-100 bg-slate-50/50"}`}
              >
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                  <span className={`font-medium ${isParentOp ? "text-amber-900" : "text-slate-800"}`}>
                    {op.label || `AO${op.id}`}
                  </span>
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${isParentOp ? "bg-amber-200/70 text-amber-900" : "bg-slate-200 text-slate-700"}`}>
                    {op.status || "—"}
                  </span>
                  {op.holderType && (
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                      op.holderType === "beneficiary"
                        ? "bg-violet-100 text-violet-700"
                        : "bg-sky-100 text-sky-700"
                    }`}
                    >
                      {op.holderType === "beneficiary" ? "Ayant droit" : "Salarié"}
                    </span>
                  )}
                </div>
                {op.beneficiary && (
                  <div className={`text-[11px] font-medium ${isParentOp ? "text-amber-800" : "text-slate-600"}`}>
                    {op.beneficiary}
                  </div>
                )}
                <div className={`text-[11px] ${isParentOp ? "text-amber-700" : "text-slate-500"}`}>
                  Solde :
                  {" "}
                  {formatAmount(op.allocationBalance ?? 0)}
                  {" "}
                  € sur
                  {" "}
                  {formatAmount((op.debit ?? 0) + (op.credit ?? 0))}
                  {" "}
                  €
                </div>

                {/* Table of allocations (non-parent ops show all, parent op shows only siblings) */}
                {(isParentOp ? siblingAllocations : op.allocations).length > 0 && (
                  <table className="w-full text-left text-[11px]">
                    <thead>
                      <tr className={`border-b ${isParentOp ? "border-amber-200 text-amber-700" : "border-slate-200 text-slate-500"}`}>
                        <th className="py-1 pr-2 font-medium whitespace-nowrap w-[90px]">Date</th>
                        <th className="py-1 pr-2 font-medium">Libellé</th>
                        <th className="py-1 pr-2 font-medium text-right whitespace-nowrap w-[70px]">Débit</th>
                        <th className="py-1 font-medium text-right whitespace-nowrap w-[70px]">Crédit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(isParentOp ? siblingAllocations : op.allocations).map((alloc) => (
                        <tr
                          key={alloc.id}
                          className={`border-b last:border-0 ${isParentOp ? "border-amber-100" : "border-slate-100"}`}
                        >
                          <td className="py-1 pr-2 tabular-nums text-slate-700 whitespace-nowrap">{alloc.date ?? ""}</td>
                          <td className="py-1 pr-2 text-slate-700">{alloc.label}</td>
                          <td className="py-1 pr-2 tabular-nums text-right text-slate-700 whitespace-nowrap">
                            {alloc.debit ? `${formatAmount(alloc.debit)} €` : ""}
                          </td>
                          <td className="py-1 tabular-nums text-right text-slate-700 whitespace-nowrap">
                            {alloc.credit ? `${formatAmount(alloc.credit)} €` : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {/* Inline editor for current allocation — only in parent op */}
                {isParentOp && (
                  <div className="rounded-md border border-amber-300 bg-white p-2.5 space-y-2 [&_label]:!text-[12px] [&_label]:font-normal [&_label]:leading-tight [&_input]:!text-[12px] [&_input]:md:!text-[12px] [&_input]:leading-tight">
                    <div className="text-[11px] font-medium text-amber-700">Écriture en cours d'édition</div>
                    <div className="grid grid-cols-2 xl:grid-cols-4 gap-2">
                      <div className="space-y-1">
                        <label className="text-[12px] leading-tight text-slate-600">Date</label>
                        <Input
                          type="date"
                          min={ACCOUNTING_DATE_BOUNDS.min}
                          max={ACCOUNTING_DATE_BOUNDS.max}
                          value={simpleEditorRow?.date ?? ""}
                          onChange={(event) => {
                            if (!selectedAllocation) return;
                            setField(selectedAllocation.id, "date", event.target.value, selectedAllocation);
                          }}
                          className="h-7 !text-[12px] md:!text-[12px] leading-tight"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[12px] leading-tight text-slate-600">Type budget</label>
                        <select
                          className="h-7 w-full rounded-md border border-slate-200 bg-white px-2 text-[12px]"
                          value={simpleEditorRow?.budgetType || "ASC"}
                          onChange={(event) => {
                            if (!selectedAllocation) return;
                            setField(selectedAllocation.id, "budgetType", event.target.value, selectedAllocation);
                          }}
                          disabled={isSimpleEditorBusy}
                        >
                          <option value="ASC">ASC</option>
                          <option value="AEP">AEP</option>
                        </select>
                      </div>
                      <div className="space-y-1">
                        <label className="text-[12px] leading-tight text-slate-600">Débit</label>
                        <Input
                          inputMode="decimal"
                          value={simpleEditorRow?.debit ?? ""}
                          onChange={(event) => {
                            if (!selectedAllocation) return;
                            setField(selectedAllocation.id, "debit", normalizeCurrencyTyping(event.target.value), selectedAllocation);
                          }}
                          onBlur={(event) => {
                            if (!selectedAllocation) return;
                            setField(selectedAllocation.id, "debit", formatCurrencyField(event.target.value), selectedAllocation);
                          }}
                          className={`h-7 !text-[12px] md:!text-[12px] leading-tight tabular-nums text-right ${isDebitLocked ? LOCKED_AMOUNT_INPUT_CLASS : ""}`}
                          placeholder="0,00"
                          readOnly={isDebitLocked}
                          aria-disabled={isDebitLocked}
                          title={isDebitLocked ? DEBIT_LOCKED_HINT : undefined}
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[12px] leading-tight text-slate-600">Crédit</label>
                        <Input
                          inputMode="decimal"
                          value={simpleEditorRow?.credit ?? ""}
                          onChange={(event) => {
                            if (!selectedAllocation) return;
                            setField(selectedAllocation.id, "credit", normalizeCurrencyTyping(event.target.value), selectedAllocation);
                          }}
                          onBlur={(event) => {
                            if (!selectedAllocation) return;
                            setField(selectedAllocation.id, "credit", formatCurrencyField(event.target.value), selectedAllocation);
                          }}
                          className={`h-7 !text-[12px] md:!text-[12px] leading-tight tabular-nums text-right ${isCreditLocked ? LOCKED_AMOUNT_INPUT_CLASS : ""}`}
                          placeholder="0,00"
                          readOnly={isCreditLocked}
                          aria-disabled={isCreditLocked}
                          title={isCreditLocked ? CREDIT_LOCKED_HINT : undefined}
                        />
                      </div>
                    </div>
                    <AllocationRoomHint
                      operationBalance={op.allocationBalance}
                      allocation={selectedAllocation}
                      disabled={isSimpleEditorBusy}
                      onApply={(debit, credit) => {
                        if (!selectedAllocation) return;
                        setField(selectedAllocation.id, "debit", debit, selectedAllocation);
                        setField(selectedAllocation.id, "credit", credit, selectedAllocation);
                      }}
                    />
                    <div className="space-y-1">
                      <label className="text-[12px] leading-tight text-slate-600">Libellé</label>
                      <Input
                        value={simpleEditorRow?.description ?? ""}
                        onChange={(event) => {
                          if (!selectedAllocation) return;
                          setField(selectedAllocation.id, "description", event.target.value, selectedAllocation);
                        }}
                        className="h-7 !text-[12px] md:!text-[12px] leading-tight"
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-0.5">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={onClose}
                        disabled={isSimpleEditorBusy}
                        className="h-7 text-[11px] px-3"
                      >
                        Annuler
                      </Button>
                      <Button
                        type="button"
                        variant="success"
                        onClick={onSave}
                        disabled={isSimpleEditorBusy}
                        className="h-7 text-[11px] px-3"
                      >
                        Enregistrer
                      </Button>
                    </div>
                  </div>
                )}

                {/* Empty state for non-parent ops */}
                {!isParentOp && op.allocations.length === 0 && (
                  <div className="text-[11px] italic text-slate-400">Aucune écriture</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </BaseDrawer>
  );
};

export default ReimbursementAllocationDetail;
